import ABCyaLib from '@abcya/abcya_lib';
import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import FontKeys from '../FontKeys';
import CounterText from '../game/CounterText';
import Emitter from '../game/Emitter';
import SpriteTarget from '../game/SpriteTarget';
import StateButton from '../game/StateButton';
import TextTimer from '../game/TextTimer';
import gameData from '../gameData';
import { isMuted, playGameJingle, stopGameJingle, toggleMute } from '../JingleController';
import SoundAssetKeys from '../SoundAssetKeys';
import UIAssetKeys from '../UIAssetKeys';

export default class Game extends Phaser.Scene {
  static get KEY() { return 'Game'; }

  constructor() {
    super({ key: Game.KEY });
  }

  create() {
    playGameJingle();

    this.eggs = [];

    const width = this.scale.width;

    ABCyaPhaser.Images.addImage(0, 0, `background${gameData.CURRENT_LEVEL}`, this).setOrigin(0);

    this.scoreboard = ABCyaPhaser.Images.addImage(this.scale.width / 2, 0, UIAssetKeys._scoreboard, this);
    this.scoreboard.y = this.scoreboard.height / 2;

    let hexColor = 0xffffff;
    switch (gameData.mode) {
      case 'halloween':
        hexColor = 0x000000;
        break;
      case 'easter':
        hexColor = 0x956232;
        break;
    }

    const level = gameData.CURRENT_LEVEL;
    let seconds = 25;
    let eggCount = 5;
    let eggAlpha = 0.9;

    /*
         L1: 5 eggs/ 25 sec
         L2: 5 eggs/ 25 sec
         L3: 7 eggs/ 35 sec
         L4: 7 eggs/ 35 sec
         L5: 9 eggs/ 45 sec
         L6: 9 eggs/ 45 sec
         L7: 12 eggs/ 60 sec
         L8: 12 eggs/ 60 sec
         L9: 15 eggs/ 75 sec
         */

    /*
         Level 1- 25 secs (stays the same)
         Level 2- 30 secs
         Level 3- 40 secs
         Level 4- 45 secs
         Level 5- 50 secs
         Level 6- 60 secs
         Level 7- 60 secs
         Level 8- 75 secs
         Level 9- 75 secs (stays the same)
         */

    switch (level) {
      case 1:
        seconds = 25;
        eggCount = 5;
        eggAlpha = 0.8;
        break;

      case 2:
        seconds = 30;
        eggCount = 6;
        eggAlpha = 0.75;
        break;

      case 3:
        seconds = 40;
        eggCount = 7;
        eggAlpha = 0.65;
        break;

      case 4:
        seconds = 45;
        eggCount = 8;
        eggAlpha = 0.55;
        break;

      case 5:
        seconds = 50;
        eggCount = 9;
        eggAlpha = 0.65;
        break;

      case 6:
        seconds = 60;
        eggCount = 10;
        eggAlpha = 0.55;
        break;

      case 7:
        seconds = 60;
        eggCount = 12;
        eggAlpha = 0.45;
        break;

      case 8:
        seconds = 75;
        eggCount = 13;
        eggAlpha = 0.5;
        break;

      case 9:
        seconds = 75;
        eggCount = 15;
        eggAlpha = 0.7;
        break;
    }

    this.textTimer = new TextTimer(this, seconds, 26, hexColor);
    this.add.existing(this.textTimer);
    this.textTimer.x = 110;
    this.textTimer.y = 10;

    this.textTimer.on('TIMER_DONE', this.timerZero, this);

    this.textLevel = new CounterText(this, '_VALUE_', 26, hexColor);
    this.add.existing(this.textLevel);
    this.textLevel.x = width / 2 + 34;
    this.textLevel.y = 10;
    switch (gameData.mode) {
      case 'christmas':
        this.textLevel.setPosition(width / 2 + 37, 7);
        break;
      case 'hanukkah':
        this.textLevel.setPosition(width / 2 + 37, 7);
        break;
      case 'shamrock':
        this.textLevel.setPosition(width / 2 + 37, 7);
        break;
      case 'valentines':
        this.textLevel.setPosition(width / 2 + 37, 7);
        break;
    }
    this.textLevel.setValue(gameData.CURRENT_LEVEL);

    this.eggsFound = new CounterText(this, `_VALUE_/${eggCount}`, 20, hexColor);
    this.add.existing(this.eggsFound);
    this.eggsFound.x = width - 60;
    this.eggsFound.y = 16;
    switch (gameData.mode) {
      case 'christmas':
        this.eggsFound.setPosition(width - 57, 13);
        break;
      case 'hanukkah':
        this.eggsFound.setPosition(width - 75, 13);
        break;
      case 'shamrock':
        this.eggsFound.setPosition(width - 57, 13);
        break;
      case 'valentines':
        this.eggsFound.setPosition(width - 59, 13);
        break;
    }
    this.eggsFound.setValue(0);

    if (gameData.CURRENT_LEVEL === 1) {
      this.time.delayedCall(1000, this.showIntroDialog, [eggCount, eggAlpha], this);
    }
    else {
      this.makeLevel(eggCount, eggAlpha);
    }

    // make toggle button for music
    this.musicButton = new StateButton(this, UIAssetKeys._speaker_on, UIAssetKeys._speaker_off);
    this.musicButton.x = this.scale.width - 176;
    switch (gameData.mode) {
      case 'christmas':
        this.musicButton.x = this.scale.width - 200;
        break;
      case 'hanukkah':
        this.musicButton.x = this.scale.width - 200;
        break;
      case 'shamrock':
        this.musicButton.x = this.scale.width - 236;
        break;
      case 'valentines':
        this.musicButton.x = this.scale.width - 230;
        break;
    }
    this.musicButton.y = 26;
    this.add.existing(this.musicButton);
    this.musicButton.doAction1 = this.speakerOff.bind(this);
    this.musicButton.doAction2 = this.speakerOn.bind(this);
    if (isMuted()) {
      this.musicButton.switchStateOff();
    }

    this.doTransition();
  }

  timerZero() {
    stopGameJingle();

    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._eggs_revealed, this);

    this.highlightEggs();
  }

  highlightEggs() {
    for (let i = 0; i < this.eggs.length; i++) {
      const egg = this.eggs[i];
      egg.alpha = 1;
      egg.disableInteractive();

      if (egg.scene !== null && egg.scene !== undefined) {
        ABCyaPhaser.Tweens.addTween(egg, 100, { scale: 1.1, yoyo: true, repeat: -1 }, Phaser.Math.Easing.Linear);

        egg.tint = 0xffff00;
      }
    }

    this.time.delayedCall(5000, this.playAgain, null, this);
  }

  playAgain() {
    stopGameJingle();
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._game_over, this);

    this.removeEggs();

    gameData.CURRENT_LEVEL = 0;

    this.textLabel = ABCyaPhaser.Text.addText(this.scale.width / 2, this.scale.height / 2, 58, 0xffffff, FontKeys._riffic_freebold, 'Play again?', this).setOrigin(0.5)
      .setResolution(2);
    this.textLabel.setStroke(0x000000, 4);

    this.button = ABCyaPhaser.Buttons.addSimpleButton(this.scale.width / 2, this.scale.height / 2 + 205, UIAssetKeys._go_button, null, () => this.cleanUp(), this);
  }

  removeEggs() {
    for (let i = 0; i < this.eggs.length; i++) {
      const egg = this.eggs[i];

      if (egg !== null && egg.scene !== undefined) {
        egg.destroy();
      }
    }
  }

  showIntroDialog(eggCount, eggAlpha) {
    this.textLabel = ABCyaPhaser.Text.addText(this.scale.width / 2, this.scale.height / 2, 58, 0xffffff, FontKeys._riffic_freebold, 'Find the hidden candy!', this).setOrigin(0.5)
      .setScale(2)
      .setResolution(2);
    switch (gameData.mode) {
      case 'christmas':
        this.textLabel.text = 'Find the hidden presents!';
        break;
      case 'easter':
        this.textLabel.text = 'Find the hidden eggs!';
        break;
      case 'hanukkah':
        this.textLabel.text = 'Find the hidden gelt!';
        break;
      case 'shamrock':
        this.textLabel.text = 'Find the hidden shamrocks!';
        break;
      case 'valentines':
        this.textLabel.text = 'Find the hidden valentines!';
        break;
    }
    this.textLabel.setStroke(0x000000, 4);

    this.tweens.timeline({
      tweens:
                [
                  ABCyaPhaser.Tweens.createTween(this.textLabel, 1000, { scale: 1 }, Phaser.Math.Easing.Bounce.Out),
                  ABCyaPhaser.Tweens.createTween(this.textLabel, 1000, { delay: 1000, scale: 4, alpha: 0.2 }, Phaser.Math.Easing.Linear),
                ],
      onComplete: () => {
        this.textLabel.destroy();
        this.makeLevel(eggCount, eggAlpha);
      }
    });
  }

  makeLevel(eggCount, eggAlpha) {
    this.textTimer.start();

    for (let i = 0; i < eggCount; i++) {
      const randXPos = Phaser.Math.Between(0, this.scale.width - 100) + 50;
      const randYPos = Phaser.Math.Between(0, this.scale.height - 100 - this.scoreboard.height) + 50 + this.scoreboard.height;

      const egg = new SpriteTarget(this, 'egg');
      this.add.existing(egg);
      egg.x = randXPos;
      egg.y = randYPos;
      egg.alpha = eggAlpha;

      switch (gameData.mode) {
        case 'easter':
          egg.alpha = eggAlpha - 0.3;
          break;
        case 'shamrock':
          egg.alpha = eggAlpha - 0.15;
          break;
        case 'valentines':
          egg.alpha = eggAlpha - 0.15;
          egg.scale = 0.8;
          break;
      }

      this.eggs.push(egg);

      egg.clickAction = this.eggClicked.bind(this);
    }

    if (ABCyaLib.Environment.instance.environment !== ABCyaLib.Environment.ENVIRONMENTS.production) {
      this.input.keyboard.on(Phaser.Input.Keyboard.Events.ANY_KEY_UP, (evt) => {
        if (evt.ctrlKey && evt.keyCode === Phaser.Input.Keyboard.KeyCodes.Z) {
          for (let i = 0; i < this.eggs.length; i++) {
            this.eggs[i].alpha = 1;
            this.eggs[i].setTintFill(0xffff00);
          }
        }
      });
    }
  }

  eggClicked(egg) {
    egg.disableInteractive();
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._found_egg, this);

    switch (gameData.mode) {
      case 'halloween':
        randomPlay(this, [SoundAssetKeys._found_egg1, SoundAssetKeys._found_egg2, SoundAssetKeys._found_egg3, SoundAssetKeys._found_egg4]);
        break;
      case 'christmas':
        randomPlay(this, [SoundAssetKeys._found_egg1, SoundAssetKeys._found_egg2]);
        break;
      case 'easter':
        this.bunnyAppears();
        break;
      case 'hanukkah':
        randomPlay(this, [SoundAssetKeys._found_egg1, SoundAssetKeys._found_egg2]);
        break;
      case 'shamrock':
        randomPlay(this, [SoundAssetKeys._found_egg1, SoundAssetKeys._found_egg2]);
        break;
      case 'valentines':
        randomPlay(this, [SoundAssetKeys._found_egg1, SoundAssetKeys._found_egg2]);
        break;
    }

    this.eggsFound.addValue(1);

    this.addParticles(egg);

    if (this.eggsFound.getValue() === this.eggs.length) {
      if (gameData.mode !== 'easter') {
        this.time.delayedCall(500, this.bunnyAppears, null, this);
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._level_completed, this, 1, false, 1.5);
      }
      else {
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._level_completed, this);
      }

      this.textTimer.stop();

      let delayDuration = 3000;
      switch (gameData.mode) {
        case 'easter':
          delayDuration = 2000;
          break;
      }
      this.time.delayedCall(delayDuration, () => {
        egg.destroy();
        this.cleanUp();
      });

    }
    else {
      this.time.delayedCall(2000, () => egg.destroy());
    }
  }

  addParticles(egg) {
    // eslint-disable-next-line no-param-reassign
    egg.alpha = 0;

    const emitter = new Emitter(this, 5);
    emitter.x = egg.x;
    emitter.y = egg.y;

    this.add.existing(emitter);
    emitter.emit('explosion', 10);

    this.children.moveTo(emitter, 1);
  }

  cleanUp() {
    gameData.CURRENT_LEVEL++;

    if (gameData.CURRENT_LEVEL >= 10) {
      this.playAgainSuccess();
      return;
    }

    this.scene.start();
  }

  playAgainSuccess() {
    stopGameJingle();
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._success, this);

    this.removeEggs();

    gameData.CURRENT_LEVEL = 0;

    this.textLabel1 = ABCyaPhaser.Text.addText(this.scale.width / 2, this.scale.height / 2 - 120, 58, 0xffffff, FontKeys._riffic_freebold, 'You found all the candy!', this).setOrigin(0.5)
      .setResolution(2);
    switch (gameData.mode) {
      case 'christmas':
        this.textLabel1.text = 'You found all the presents!';
        break;
      case 'easter':
        this.textLabel1.text = 'You found all the eggs!';
        break;
      case 'hanukkah':
        this.textLabel1.text = 'You found all the gelt!';
        break;
      case 'shamrock':
        this.textLabel1.text = 'You found all the shamrocks!';
        break;
      case 'valentines':
        this.textLabel1.text = 'You found all the valentines!';
        break;
    }
    this.textLabel1.setStroke(0x000000, 4);
    this.textLabel = ABCyaPhaser.Text.addText(this.scale.width / 2, this.scale.height / 2, 58, 0xffffff, FontKeys._riffic_freebold, 'Play again?', this).setOrigin(0.5)
      .setResolution(2);
    this.textLabel.setStroke(0x000000, 4);
    this.button = ABCyaPhaser.Buttons.addSimpleButton(this.scale.width / 2, this.scale.height / 2 + 205, UIAssetKeys._go_button, null, () => this.cleanUp(), this);
  }

  speakerOff() {
    toggleMute(true);
  }


  speakerOn() {
    toggleMute(false);
  }

  doTransition() {
    this.cameras.main.x = 1200;
    ABCyaPhaser.Tweens.addTween(this.cameras.main, 400, { x: 0 }, Phaser.Math.Easing.Linear);
  }

  bunnyAppears() {
    const bunny = ABCyaPhaser.Images.addImage(0, 0, UIAssetKeys._bunny, this);

    const offset = bunny.height;
    const x = Phaser.Math.Between(300, this.scale.width - 300);
    const y = this.scale.height + offset + 100;

    bunny.x = x;
    bunny.y = y;

    switch (gameData.mode) {
      case 'valentines':
        bunny.y -= 150;
        break;
    }

    switch (gameData.mode) {
      case 'halloween':
        ABCyaPhaser.Tweens.addTween(bunny, 2500, { alpha: 0, y: 100 }, Phaser.Math.Easing.Linear, () => bunny.destroy());
        ABCyaPhaser.Tweens.addTween(bunny, 300, { scaleX: 0.9, scaleY: 1.1, repeat: 3, yoyo: true }, Phaser.Math.Easing.Linear);
        break;
      case 'christmas':
        ABCyaPhaser.Tweens.addTween(bunny, 300, { y: this.scale.height - 200 }, Phaser.Math.Easing.Linear);
        break;
      case 'easter':
        // note: the easter game has the bunny appear from a random side of the screen, whereas the other versions only show the "bunny" from the bottom
        const num = Phaser.Math.Between(0,3);
        if (num === 1) { // top

          bunny.angle = 180;

          bunny.x = Phaser.Math.Between(100, this.scale.width - 100);
          bunny.y = -offset;

          ABCyaPhaser.Tweens.addTween(bunny, 300, { y: bunny.y + 1.5 * offset }, Phaser.Math.Easing.Linear);
          ABCyaPhaser.Tweens.addTween(bunny, 900, { y: bunny.y, delay: 500 }, Phaser.Math.Easing.Linear);
        }
        else if (num === 2) { // left

          bunny.angle = 90;

          bunny.x = -offset;
          bunny.y = Phaser.Math.Between(100, this.scale.height - 100);

          ABCyaPhaser.Tweens.addTween(bunny, 300, { x: bunny.x + 1.5 * offset }, Phaser.Math.Easing.Linear);
          ABCyaPhaser.Tweens.addTween(bunny, 900, { y: bunny.x, delay: 500 }, Phaser.Math.Easing.Linear);
        }
        else if (num === 3) { // right

          bunny.angle = -90;

          bunny.x = this.scale.width + offset;
          bunny.y = Phaser.Math.Between(100, this.scale.height - 100);

          ABCyaPhaser.Tweens.addTween(bunny, 300, { x: bunny.x - 1.5 * offset }, Phaser.Math.Easing.Linear);
          ABCyaPhaser.Tweens.addTween(bunny, 900, { y: bunny.x, delay: 500 }, Phaser.Math.Easing.Linear);
        }
        else {// bottom (default)
          bunny.x = Phaser.Math.Between(100, this.scale.width - 100);
          bunny.y = this.scale.height + offset;

          ABCyaPhaser.Tweens.addTween(bunny, 300, { y: bunny.y - 1.5 * offset }, Phaser.Math.Easing.Linear);
          ABCyaPhaser.Tweens.addTween(bunny, 900, { y: bunny.y, delay: 500 }, Phaser.Math.Easing.Linear);
        }
        break;
      case 'hanukkah':
        ABCyaPhaser.Tweens.addTween(bunny, 300, { y: this.scale.height - 200 }, Phaser.Math.Easing.Linear);
        break;
      case 'shamrock':
        ABCyaPhaser.Tweens.addTween(bunny, 300, { y: this.scale.height - 200 }, Phaser.Math.Easing.Linear);
        break;
      case 'valentines':
        ABCyaPhaser.Tweens.addTween(bunny, 300, { y: this.scale.height - 150 }, Phaser.Math.Easing.Linear);
        break;
    }
  }
}

/**
 * 
 * @param {Phaser.Scene} scene 
 * @param {string[]} sounds 
 */
function randomPlay(scene, sounds) {
  const randIndex = Phaser.Math.Between(0, sounds.length - 1);
  const soundName = sounds[randIndex];

  // don't play the audio if any audio in the sounds array are currently playing
  const playingSounds = scene.sound.getAll(ABCyaPhaser.Audio.DEFAULT_AUDIO_SPRITE);
  for (let i = 0; i < sounds.length; i++) {
    if (playingSounds.find(sound => sound?.currentMarker?.name === sounds[i]) !== undefined) {
      return;
    }
  }

  ABCyaPhaser.Audio.playOneShot(soundName, scene);
}